import React, { useEffect, useState, useMemo } from 'react';
import { useHorarioForm } from '../hooks/useHorarioForm';
import { DiaSemana, DIA_LABEL, DIA_COLOR, DIAS_SEMANA, TipoHorario } from '../types/horario.types';
import { AsignacionItem } from '../types/matricula.types';

// ============================================================
// HorarioFormPage – Registro / Modificación de Horario
// ============================================================

interface Props {
  idHorario?: number;
  onSuccess?: () => void;
  onCancel?:  () => void;
}

const SpinnerIcon = ({ cls = 'w-4 h-4' }: { cls?: string }) => (
  <svg className={`animate-spin ${cls}`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 10 10" className="w-3 h-3">
    <path d="M2 5l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

const FieldWrapper: React.FC<{ label: string; required?: boolean; error?: string; children: React.ReactNode }> =
  ({ label, required, error, children }) => (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && <span className="text-red-500 text-xs">{error}</span>}
    </div>
  );

// ── Colores y etiquetas de nivel ─────────────────────────────
const NIVEL_OPTS = ['INICIAL', 'PRIMARIA', 'SECUNDARIA'] as const;
const NIVEL_LABEL_MAP: Record<string, string> = {
  INICIAL: 'Inicial', PRIMARIA: 'Primaria', SECUNDARIA: 'Secundaria',
};
const NIVEL_COLOR_MAP: Record<string, { btn: string; active: string; badge: string }> = {
  INICIAL:    { btn: 'border-amber-300 text-amber-700 hover:bg-amber-50',   active: 'bg-amber-500 border-amber-500 text-white',   badge: 'bg-amber-100 text-amber-700' },
  PRIMARIA:   { btn: 'border-blue-300 text-blue-700 hover:bg-blue-50',     active: 'bg-blue-600 border-blue-600 text-white',     badge: 'bg-blue-100 text-blue-700'   },
  SECUNDARIA: { btn: 'border-purple-300 text-purple-700 hover:bg-purple-50', active: 'bg-purple-600 border-purple-600 text-white', badge: 'bg-purple-100 text-purple-700' },
};

// ── Selector visual de nivel ──────────────────────────────────
const NivelSelector: React.FC<{
  value: string;
  onChange: (nivel: string) => void;
  label?: string;
}> = ({ value, onChange, label }) => (
  <div className="flex flex-col gap-1.5">
    {label && (
      <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">{label}</p>
    )}
    <div className="flex gap-2">
      {NIVEL_OPTS.map(n => {
        const c = NIVEL_COLOR_MAP[n];
        const isActive = value === n;
        return (
          <button key={n} type="button" onClick={() => onChange(isActive ? '' : n)}
            className={`flex-1 py-2 px-3 rounded-xl border-2 text-xs font-bold transition-all duration-150
              ${isActive ? c.active : `bg-white ${c.btn}`}`}>
            {NIVEL_LABEL_MAP[n]}
          </button>
        );
      })}
    </div>
  </div>
);

// ── Picker de asignación académica ────────────────────────────
// Paso 0: filtrar por nivel (opcional)
// Paso 1: elegir docente (con búsqueda)
// Paso 2: elegir curso del docente seleccionado
const AsignacionPicker: React.FC<{
  asignaciones:       AsignacionItem[];
  loading:            boolean;
  errorCarga:         boolean;
  onReintentar:       () => void;
  value:              string;
  onChange:           (id: string) => void;
  error?:             string;
}> = ({ asignaciones, loading, errorCarga, onReintentar, value, onChange, error }) => {
  const [nivelFilter, setNivelFilter] = useState('');
  const [search,      setSearch]      = useState('');
  const [docenteId,   setDocenteId]   = useState<number | null>(null);

  // Al cargar en modo edición: preseleccionar nivel y docente a partir del value
  useEffect(() => {
    if (value && asignaciones.length > 0) {
      const a = asignaciones.find(a => String(a.idAsignacion) === value);
      if (a) {
        setDocenteId(a.docenteId);
        setNivelFilter(a.gradoNivel ?? '');
      }
    }
  }, [value, asignaciones]);

  // Asignaciones filtradas por nivel
  const asignacionesFiltradas = useMemo(() =>
    nivelFilter ? asignaciones.filter(a => a.gradoNivel === nivelFilter) : asignaciones,
    [asignaciones, nivelFilter]
  );

  // Lista de docentes únicos (del nivel seleccionado)
  const docentes = useMemo(() => {
    const map = new Map<number, {
      id: number; nombres: string; apellidos: string; count: number;
    }>();
    asignacionesFiltradas.forEach(a => {
      if (!map.has(a.docenteId)) {
        map.set(a.docenteId, {
          id: a.docenteId,
          nombres:   a.docenteNombres,
          apellidos: a.docenteApellidos,
          count: 0,
        });
      }
      map.get(a.docenteId)!.count++;
    });
    return Array.from(map.values()).sort((a, b) =>
      `${a.apellidos} ${a.nombres}`.localeCompare(`${b.apellidos} ${b.nombres}`)
    );
  }, [asignacionesFiltradas]);

  // Docentes filtrados por búsqueda
  const docentesFiltrados = useMemo(() => {
    if (!search.trim()) return docentes;
    const q = search.toLowerCase();
    return docentes.filter(d =>
      `${d.nombres} ${d.apellidos}`.toLowerCase().includes(q)
    );
  }, [docentes, search]);

  // Asignaciones del docente seleccionado (respetando filtro de nivel)
  const asignacionesDocente = useMemo(() =>
    docenteId ? asignacionesFiltradas.filter(a => a.docenteId === docenteId) : [],
    [asignacionesFiltradas, docenteId]
  );

  const asignacionActual = asignaciones.find(a => String(a.idAsignacion) === value);

  const handleNivelChange = (nivel: string) => {
    setNivelFilter(nivel);
    setDocenteId(null);
    setSearch('');
    // Limpiar selección si el nivel del curso actual difiere
    if (nivel && asignacionActual && asignacionActual.gradoNivel !== nivel) onChange('');
  };

  const handleSelectDocente = (id: number) => {
    setDocenteId(id);
    const current = asignaciones.find(a => String(a.idAsignacion) === value);
    if (current && current.docenteId !== id) onChange('');
  };

  const handleClear = () => {
    onChange('');
    setDocenteId(null);
    setSearch('');
    setNivelFilter('');
  };

  return (
    <div className="flex flex-col gap-2.5">
      <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">
        Asignación Académica <span className="text-red-500">*</span>
      </p>

      {/* ── Filtro de nivel ── */}
      {!loading && !errorCarga && !asignacionActual && (
        <NivelSelector value={nivelFilter} onChange={handleNivelChange} />
      )}

      {/* ── Estado de carga ── */}
      {loading && (
        <div className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 rounded-xl">
          <SpinnerIcon cls="w-5 h-5 text-blue-600" />
          <span className="text-sm text-slate-500">Cargando asignaciones académicas…</span>
        </div>
      )}

      {/* ── Error de carga / timeout ── */}
      {!loading && errorCarga && (
        <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-amber-500 flex-shrink-0">
            <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd"/>
          </svg>
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-800">No se pudo cargar las asignaciones</p>
            <p className="text-xs text-amber-600 mt-0.5">El servidor tardó demasiado en responder (CORS / timeout).</p>
          </div>
          <button type="button" onClick={onReintentar}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold
                       bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-300 transition-colors">
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
              <path fillRule="evenodd" d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z" clipRule="evenodd"/>
            </svg>
            Reintentar
          </button>
        </div>
      )}

      {/* ── Chip de selección confirmada ── */}
      {!loading && !errorCarga && asignacionActual && (
        <div className="flex items-center gap-3 p-3.5 bg-green-50 border-2 border-green-300 rounded-xl">
          <div className="w-10 h-10 rounded-xl bg-green-600 text-white flex items-center justify-center
                          flex-shrink-0 text-xs font-bold leading-none">
            {asignacionActual.cursoNombre.substring(0, 2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-bold text-green-800 text-sm leading-tight truncate">
                {asignacionActual.cursoNombre}
              </p>
              {asignacionActual.gradoNivel && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0
                  ${NIVEL_COLOR_MAP[asignacionActual.gradoNivel]?.badge ?? 'bg-slate-100 text-slate-600'}`}>
                  {NIVEL_LABEL_MAP[asignacionActual.gradoNivel] ?? asignacionActual.gradoNivel}
                </span>
              )}
            </div>
            <p className="text-green-600 text-xs mt-0.5 truncate">
              {asignacionActual.docenteNombres} {asignacionActual.docenteApellidos}
              {' · '}{asignacionActual.gradoNombre} {asignacionActual.seccionNombre}
              {' · '}{asignacionActual.anio}
            </p>
          </div>
          <button type="button" onClick={handleClear}
            title="Cambiar selección"
            className="p-1.5 rounded-lg text-green-600 hover:bg-green-200 hover:text-green-800 transition-colors flex-shrink-0">
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 1-1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z"/>
            </svg>
          </button>
        </div>
      )}

      {/* ── Picker (docentes + cursos) — solo cuando los datos están listos y no hay chip ── */}
      {!loading && !errorCarga && !asignacionActual && (
        <div className="border border-slate-200 rounded-xl overflow-hidden">

          {/* ── Paso 1: Buscar y seleccionar docente ── */}
          <div className="p-3 bg-slate-50 border-b border-slate-200">
            <div className="flex items-center gap-2 mb-2.5">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600
                               text-white text-[10px] font-bold flex-shrink-0">1</span>
              <span className="text-xs font-semibold text-slate-600">Selecciona un docente</span>
              {docenteId && (
                <span className="ml-auto text-xs text-blue-600 font-semibold">
                  {(() => {
                    const d = docentes.find(d => d.id === docenteId);
                    return d ? `${d.nombres} ${d.apellidos}` : '';
                  })()}
                </span>
              )}
            </div>

            {/* Búsqueda */}
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                  <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z" clipRule="evenodd"/>
                </svg>
              </span>
              <input
                type="text"
                placeholder="Buscar por nombre o apellido..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-2 rounded-lg border border-slate-200 bg-white
                           text-xs text-slate-800 placeholder-slate-300 outline-none
                           focus:ring-2 focus:ring-blue-500 transition-all"
              />
              {search && (
                <button type="button" onClick={() => setSearch('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                    <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 1-1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z"/>
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Lista de docentes */}
          <div className="max-h-48 overflow-y-auto">
            {asignaciones.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-xs text-amber-600 font-medium">Sin asignaciones registradas.</p>
                <p className="text-xs text-slate-400 mt-0.5">Primero asigna cursos a un docente.</p>
              </div>
            ) : docentesFiltrados.length === 0 ? (
              <div className="py-6 text-center text-xs text-slate-400">
                No se encontraron docentes con ese nombre.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {docentesFiltrados.map(d => {
                  const isSelected = docenteId === d.id;
                  return (
                    <button key={d.id} type="button"
                      onClick={() => handleSelectDocente(d.id)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors
                        ${isSelected
                          ? 'bg-blue-50 hover:bg-blue-100'
                          : 'bg-white hover:bg-slate-50'
                        }`}
                    >
                      {/* Avatar */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                                       text-[11px] font-bold
                        ${isSelected ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'}`}>
                        {d.nombres.charAt(0)}{d.apellidos.charAt(0)}
                      </div>

                      {/* Nombre */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold truncate leading-tight
                          ${isSelected ? 'text-blue-800' : 'text-slate-800'}`}>
                          {d.apellidos}, {d.nombres}
                        </p>
                        <p className={`text-xs mt-0.5 ${isSelected ? 'text-blue-500' : 'text-slate-400'}`}>
                          {d.count} curso{d.count !== 1 ? 's' : ''} asignado{d.count !== 1 ? 's' : ''}
                        </p>
                      </div>

                      {/* Check indicator */}
                      {isSelected ? (
                        <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                          <CheckIcon />
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-slate-200 flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Paso 2: Seleccionar curso del docente ── */}
          {docenteId && asignacionesDocente.length > 0 && (
            <div className="border-t-2 border-slate-200 bg-white">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-600
                                 text-white text-[10px] font-bold flex-shrink-0">2</span>
                <span className="text-xs font-semibold text-slate-600">
                  Selecciona el curso a asignar
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3">
                {asignacionesDocente.map(a => {
                  const isSelected = String(a.idAsignacion) === value;
                  return (
                    <button key={a.idAsignacion} type="button"
                      onClick={() => onChange(String(a.idAsignacion))}
                      className={`flex items-center gap-2.5 p-3 rounded-xl border-2 text-left
                                  transition-all duration-150
                        ${isSelected
                          ? 'border-green-500 bg-green-50 shadow-sm'
                          : 'border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                        }`}
                    >
                      {/* Icono curso */}
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0
                                       text-xs font-bold leading-none
                        ${isSelected ? 'bg-green-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                        {a.cursoNombre.substring(0, 2).toUpperCase()}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold truncate leading-tight
                          ${isSelected ? 'text-green-800' : 'text-slate-800'}`}>
                          {a.cursoNombre}
                        </p>
                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                          {a.gradoNivel && (
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0
                              ${NIVEL_COLOR_MAP[a.gradoNivel]?.badge ?? 'bg-slate-100 text-slate-600'}`}>
                              {NIVEL_LABEL_MAP[a.gradoNivel] ?? a.gradoNivel}
                            </span>
                          )}
                          <p className={`text-xs truncate
                            ${isSelected ? 'text-green-600' : 'text-slate-400'}`}>
                            {a.gradoNombre} {a.seccionNombre} · {a.anio}
                          </p>
                        </div>
                      </div>

                      {/* Check */}
                      {isSelected && (
                        <div className="w-5 h-5 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0 ml-auto">
                          <CheckIcon />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {error && <span className="text-red-500 text-xs">{error}</span>}
    </div>
  );
};

// ── Selector de tipo (CLASE / RECREO) ────────────────────────
const TipoSelector: React.FC<{ value: TipoHorario; onChange: (t: TipoHorario) => void; disabled?: boolean }> =
  ({ value, onChange, disabled }) => (
    <div className="flex gap-2">
      {(['CLASE', 'RECREO'] as TipoHorario[]).map(t => (
        <button
          key={t}
          type="button"
          disabled={disabled}
          onClick={() => onChange(t)}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border-2
                      text-sm font-bold transition-all duration-150 disabled:opacity-50
            ${value === t
              ? t === 'CLASE'
                ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                : 'border-green-600 bg-green-600 text-white shadow-sm'
              : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50'
            }`}
        >
          {t === 'CLASE' ? (
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path d="M10.394 2.08a1 1 0 0 0-.788 0l-7 3a1 1 0 0 0 0 1.84L5.25 8.051a.999.999 0 0 1 .356-.257l4-1.714a1 1 0 1 1 .788 1.838L7.667 9.088l1.94.831a1 1 0 0 0 .787 0l7-3a1 1 0 0 0 0-1.838l-7-3ZM3.31 9.397 5 10.12v4.102a8.969 8.969 0 0 0-1.05-.174 1 1 0 0 1-.89-.89 11.115 11.115 0 0 1 .01-3.128A1.001 1.001 0 0 1 3.31 9.397Zm5.99 1.9a1 1 0 0 1-.738-.32 11.12 11.12 0 0 0-4.192-3.088 1 1 0 0 1 .786-1.845 13.12 13.12 0 0 1 4.93 3.627 1 1 0 0 1-.786 1.626Zm.44 4.644a17.958 17.958 0 0 1-4.285-3.743 1 1 0 0 1 .072-1.406 11.125 11.125 0 0 0 3.215-4.952 1 1 0 0 1 1.903.623 13.12 13.12 0 0 1-3.786 5.83l3.74 3.27a1 1 0 0 1-1.859.378Z"/>
            </svg>
          ) : (
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-13a.75.75 0 0 0-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 0 0 0-1.5h-3.25V5Z" clipRule="evenodd"/>
            </svg>
          )}
          {t === 'CLASE' ? 'Clase' : 'Recreo'}
        </button>
      ))}
    </div>
  );

// ── Componente principal ──────────────────────────────────────
const HorarioFormPage: React.FC<Props> = ({ idHorario, onSuccess = () => {}, onCancel = () => {} }) => {
  const isEditing = idHorario !== undefined;

  const {
    form, errors, submitting, submitError, loadingInit,
    asignaciones, loadingAsignaciones, errorAsignaciones, reintentarAsignaciones,
    añosEscolares, todosGrados, seccionesFiltradas,
    handleChange, setDia, setTipo, handleSubmit, loadHorario,
  } = useHorarioForm(idHorario ?? null, onSuccess);

  // Nivel UI-only para filtrar grados en RECREO (no va al payload)
  // Debe estar antes de cualquier early return para cumplir las reglas de hooks
  const [nivelRecreo, setNivelRecreo] = useState('');

  useEffect(() => { loadHorario(); }, [loadHorario]);

  if (loadingInit) {
    return <div className="flex justify-center items-center h-64"><SpinnerIcon cls="w-6 h-6 text-blue-600" /></div>;
  }

  const duracion = (() => {
    if (!form.horaInicio || !form.horaFin) return 0;
    const [h1, m1] = form.horaInicio.split(':').map(Number);
    const [h2, m2] = form.horaFin.split(':').map(Number);
    return (h2 * 60 + m2) - (h1 * 60 + m1);
  })();

  const gradosFiltradosNivel = nivelRecreo
    ? todosGrados.filter(g => g.nivel === nivelRecreo)
    : todosGrados;

  const handleNivelRecreoChange = (nivel: string) => {
    setNivelRecreo(nivel);
    handleChange({ target: { name: 'gradoId',   value: '' } } as React.ChangeEvent<HTMLSelectElement>);
    handleChange({ target: { name: 'seccionId', value: '' } } as React.ChangeEvent<HTMLSelectElement>);
  };

  // Para el preview mostramos curso o "Recreo" según tipo
  const previewListo = form.horaInicio && form.horaFin && duracion > 0 &&
    (form.tipo === 'RECREO'
      ? (form.gradoId && form.seccionId)
      : form.asignacionAcademicaId);
  const previewColor = form.tipo === 'RECREO'
    ? 'from-green-50 to-emerald-50 border-green-100'
    : 'from-blue-50 to-indigo-50 border-blue-100';
  const previewBg = form.tipo === 'RECREO' ? 'bg-green-600' : 'bg-blue-600';
  const previewText = form.tipo === 'RECREO' ? 'text-green-800' : 'text-blue-800';
  const previewSub  = form.tipo === 'RECREO' ? 'text-green-500' : 'text-blue-500';

  return (
    <div className="max-w-3xl mx-auto">

      {/* Cabecera */}
      <div className="mb-6 flex items-center gap-3">
        <button onClick={onCancel}
          className="p-2 rounded-lg text-slate-400 hover:text-blue-700 hover:bg-slate-100 transition-colors">
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
            <path fillRule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd"/>
          </svg>
        </button>
        <div>
          <h1 className="text-xl font-bold text-slate-800">
            {isEditing ? 'Modificar Horario' : 'Registrar Horario'}
          </h1>
          <p className="text-slate-400 text-sm">
            {isEditing ? 'Actualiza los datos del horario' : 'Configura un horario de clase o recreo'}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className={`px-6 py-4 bg-gradient-to-r ${form.tipo === 'RECREO' ? 'from-green-800 to-green-600' : 'from-blue-900 to-blue-700'}`}>
          <h2 className="text-white font-bold">Datos del Horario</h2>
          <p className="text-white/70 text-xs mt-0.5">
            {form.tipo === 'RECREO' ? 'Recreo para un grado y sección' : 'Docente, curso, día y horas de la clase'}
          </p>
        </div>

        <div className="p-6 space-y-6">

          {/* Selector de tipo */}
          <FieldWrapper label="Tipo de Horario" required>
            <TipoSelector value={form.tipo} onChange={setTipo} disabled={isEditing} />
            {isEditing && (
              <p className="text-xs text-slate-400 mt-1">El tipo no se puede cambiar en modo edición.</p>
            )}
          </FieldWrapper>

          <div className="border-t border-slate-100" />

          {/* ── Sección CLASE: AsignacionPicker ── */}
          {form.tipo === 'CLASE' && (
            <AsignacionPicker
              asignaciones={asignaciones}
              loading={loadingAsignaciones}
              errorCarga={errorAsignaciones}
              onReintentar={reintentarAsignaciones}
              value={form.asignacionAcademicaId}
              onChange={id =>
                handleChange({
                  target: { name: 'asignacionAcademicaId', value: id },
                } as React.ChangeEvent<HTMLSelectElement>)
              }
              error={errors.asignacionAcademicaId}
            />
          )}

          {/* ── Sección RECREO: Año / Grado / Sección ── */}
          {form.tipo === 'RECREO' && (
            <div className="space-y-4">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                Aplicar a <span className="text-red-500">*</span>
              </p>

              {/* Nivel (filtro visual) */}
              <NivelSelector
                value={nivelRecreo}
                onChange={handleNivelRecreoChange}
                label="Nivel *"
              />

              {/* Año escolar */}
              <FieldWrapper label="Año Escolar" required error={errors.anioEscolarId}>
                <select
                  name="anioEscolarId"
                  value={form.anioEscolarId}
                  onChange={handleChange}
                  className={`w-full border rounded-xl px-3 py-2.5 text-sm text-slate-800 bg-slate-50
                    outline-none focus:ring-2 focus:ring-green-500 transition-all
                    ${errors.anioEscolarId ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}
                >
                  <option value="">— Seleccionar año escolar —</option>
                  {añosEscolares.map(a => (
                    <option key={a.idAnioEscolar} value={a.idAnioEscolar}>
                      {a.anio}{a.activo ? ' (activo)' : ''}
                    </option>
                  ))}
                </select>
              </FieldWrapper>

              <div className="grid grid-cols-2 gap-4">
                {/* Grado */}
                <FieldWrapper label="Grado" required error={errors.gradoId}>
                  <select
                    name="gradoId"
                    value={form.gradoId}
                    onChange={handleChange}
                    disabled={!nivelRecreo}
                    className={`w-full border rounded-xl px-3 py-2.5 text-sm text-slate-800 bg-slate-50
                      outline-none focus:ring-2 focus:ring-green-500 transition-all
                      disabled:opacity-50 disabled:cursor-not-allowed
                      ${errors.gradoId ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}
                  >
                    <option value="">— Seleccionar grado —</option>
                    {gradosFiltradosNivel.map(g => (
                      <option key={g.idGrado} value={g.idGrado}>{g.nombre}</option>
                    ))}
                  </select>
                </FieldWrapper>

                {/* Sección */}
                <FieldWrapper label="Sección" required error={errors.seccionId}>
                  <select
                    name="seccionId"
                    value={form.seccionId}
                    onChange={handleChange}
                    disabled={!form.gradoId}
                    className={`w-full border rounded-xl px-3 py-2.5 text-sm text-slate-800 bg-slate-50
                      outline-none focus:ring-2 focus:ring-green-500 transition-all
                      disabled:opacity-50 disabled:cursor-not-allowed
                      ${errors.seccionId ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}
                  >
                    <option value="">— Seleccionar sección —</option>
                    {seccionesFiltradas.map(s => (
                      <option key={s.idSeccion} value={s.idSeccion}>{s.nombre}</option>
                    ))}
                  </select>
                </FieldWrapper>
              </div>
            </div>
          )}

          <div className="border-t border-slate-100" />

          {/* Selector de día — solo para CLASE */}
          {form.tipo === 'CLASE' && (
            <FieldWrapper label="Día de la Semana" required error={errors.dia}>
              <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                {DIAS_SEMANA.map(dia => (
                  <button
                    key={dia}
                    type="button"
                    onClick={() => setDia(dia as DiaSemana)}
                    className={`py-3 px-1 rounded-xl border-2 text-xs font-bold transition-all duration-150 text-center
                      ${form.dia === dia
                        ? DIA_COLOR[dia] + ' border-current shadow-sm scale-[1.04]'
                        : 'border-slate-200 text-slate-500 hover:border-slate-300 bg-white hover:bg-slate-50'
                      }`}
                  >
                    {DIA_LABEL[dia].substring(0, 3).toUpperCase()}
                  </button>
                ))}
              </div>
              {form.dia && (
                <p className="text-xs text-slate-400 mt-1">
                  Día seleccionado:{' '}
                  <span className="font-semibold text-slate-600">{DIA_LABEL[form.dia]}</span>
                </p>
              )}
            </FieldWrapper>
          )}

          {/* Aviso recreo — se creará un registro por cada día de la semana */}
          {form.tipo === 'RECREO' && (
            <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5">
                <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clipRule="evenodd"/>
              </svg>
              <p className="text-green-700 text-xs">
                El recreo se registrará automáticamente para <strong>todos los días de la semana</strong> (Lunes a Sábado) con la misma franja horaria.
              </p>
            </div>
          )}

          {/* Horas */}
          <div className="grid grid-cols-2 gap-4">
            <FieldWrapper label="Hora de Inicio" required error={errors.horaInicio}>
              <input
                type="time"
                name="horaInicio"
                value={form.horaInicio}
                onChange={handleChange}
                className={`w-full px-3 py-2.5 rounded-lg border text-sm text-slate-800 bg-slate-50
                  outline-none focus:ring-2 focus:ring-blue-500 transition-all
                  ${errors.horaInicio ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}
              />
            </FieldWrapper>

            <FieldWrapper label="Hora de Fin" required error={errors.horaFin}>
              <input
                type="time"
                name="horaFin"
                value={form.horaFin}
                onChange={handleChange}
                className={`w-full px-3 py-2.5 rounded-lg border text-sm text-slate-800 bg-slate-50
                  outline-none focus:ring-2 focus:ring-blue-500 transition-all
                  ${errors.horaFin ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}
              />
            </FieldWrapper>
          </div>

          {/* Preview */}
          {previewListo && (
            <div className={`flex items-center gap-4 bg-gradient-to-r ${previewColor} border rounded-xl p-4`}>
              <div className={`w-11 h-11 rounded-xl ${previewBg} text-white flex items-center justify-center flex-shrink-0`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
                </svg>
              </div>
              <div>
                <p className={`${previewText} font-bold text-sm`}>
                  {form.tipo === 'RECREO'
                    ? `Recreo · Lun–Sáb · ${form.horaInicio} – ${form.horaFin}`
                    : `${DIA_LABEL[form.dia]} · ${form.horaInicio} – ${form.horaFin}`}
                </p>
                <p className={`${previewSub} text-xs mt-0.5`}>
                  {Math.floor(duracion / 60) > 0 && `${Math.floor(duracion / 60)}h `}
                  {duracion % 60 > 0 && `${duracion % 60}min`}
                  {' de duración'}
                </p>
              </div>
            </div>
          )}

          {submitError && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5Zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd"/>
              </svg>
              <span className="text-red-600 text-sm">{submitError}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <button onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-200 transition-colors">
            Cancelar
          </button>
          <button onClick={handleSubmit} disabled={submitting}
            className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold
                       text-white transition-colors shadow-sm disabled:opacity-60
                       ${form.tipo === 'RECREO' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
            {submitting ? <SpinnerIcon /> : (
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd"/>
              </svg>
            )}
            {isEditing ? 'Guardar Cambios' : 'Registrar Horario'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HorarioFormPage;
